import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Tooltip,
  Collapse,
  Tab,
  Tabs,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import {
  setQaData,
  setUseCases,
  setMetrics,
  setApplicationSummary,
  setTrustWeights,
  setRisks,
  setDatasetData,
  setLoading as setDatasetLoading,
  setError as setDatasetError,
} from "../store/slices/datasetSlice";
import LoadingOverlay from "../components/LoadingOverlay";
import ProjectRequired from "../components/ProjectRequired";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ReactComponent as UploadSVG } from "../asserts/logo/upload.svg";
import { ReactComponent as DatasourceSVG } from "../asserts/logo/Datasource.svg";
import { ReactComponent as InputsSVG } from "../asserts/logo/Inputs.svg";
import { ReactComponent as ErrorIcon } from "../asserts/logo/ErrorIcon.svg";
import { ReactComponent as DownloadSVG } from "../asserts/logo/download.svg";

// DOCX export libraries
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  TextRun,
  WidthType,
  AlignmentType,
  BorderStyle,
} from "docx";

const SIDEBAR_WIDTH = 0;

const UseCaseDatasetGenerationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const { projectId } = useParams();
  const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";

  /** 🔧 Normalize dataset fields to ensure consistency */
  const normalizeDatasetItem = (item) => ({
    use_case: item.use_case,
    input_prompt: item.input_prompt || item.question,
    expected_output: item.expected_output || item.expected_response || item.answer,
    metrics: item.metrics,
    difficulty: item.difficulty || ["Low", "Medium", "Critical"][Math.floor(Math.random() * 3)],
    status: item.status || "Draft",
    source_kb: item.source_kb || item.source || "Knowledge Base",
    policy_id: item.policy_id || "",
  });

  // Get data from location.state (navigation) or Redux (persisted)
  const locationData = location.state || {};

  // Redux state
  const dataset = useSelector((state) => state.dataset.qaData);
  const reduxUseCases = useSelector((state) => state.dataset.useCases);
  const reduxMetrics = useSelector((state) => state.dataset.metrics);
  const reduxApplicationSummary = useSelector(
    (state) => state.dataset.applicationSummary
  );
  const reduxTrustWeights = useSelector((state) => state.dataset.trustWeights);
  const reduxRisks = useSelector((state) => state.dataset.risks);
  const loading = useSelector((state) => state.dataset.loading);
  const error = useSelector((state) => state.dataset.error);
  const projectId = useSelector((state) => state.project.currentProjectId);

  // Fallback to Redux if location.state is empty
  const incomingUseCases = locationData.useCases || reduxUseCases || [];
  const useCases = reduxUseCases.length > 0 ? reduxUseCases : incomingUseCases;
  const metrics = locationData.metrics || reduxMetrics || [];
  const applicationSummary =
    locationData.applicationSummary || reduxApplicationSummary || {};
  const trustWeights = locationData.trustWeights || reduxTrustWeights || {};
  const risks = locationData.risks || reduxRisks || [];

  // Local UI state (not persisted)
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [qaData, setLocalQaData] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedUseCases, setSelectedUseCases] = useState([]);
  const [useCasesExpanded, setUseCasesExpanded] = useState(true);

  // Manual Test Suite Upload state
  const [uploadedTestSuiteFiles, setUploadedTestSuiteFiles] = useState([]);
  const [manualTestData, setManualTestData] = useState([]);
  const [activeTestTab, setActiveTestTab] = useState(0); // 0 = Generated, 1 = Manual/Uploaded

  // Fetch data from database on mount
  useEffect(() => {
    const fetchDatasetData = async () => {
      if (!projectId) {
        console.log("No projectId, skipping fetch");
        return;
      }

      try {
        dispatch(setDatasetLoading(true));
        const response = await axios.get(
          `${baseUrl}/dataset-generation?project_id=${projectId}`
        );

        if (response.data.success && response.data.data) {
          console.log("Fetched dataset data:", response.data.data);
          dispatch(setDatasetData(response.data.data));

          // Handle both 'dataset' and 'qaDataset' field names
          const datasetArray =
            response.data.data.qaDataset || response.data.data.dataset || [];
          if (datasetArray.length > 0) {
            // 🔧 Normalize the data to ensure consistent field names
            const normalizedData = datasetArray.map(normalizeDatasetItem);
            setLocalQaData(normalizedData);
            dispatch(setQaData(normalizedData));
            console.log(
              "📊 Loaded and normalized dataset with",
              normalizedData.length,
              "entries"
            );
          }
        }
      } catch (err) {
        console.error("Error fetching dataset:", err);
        dispatch(setDatasetError("Failed to load data"));
      } finally {
        dispatch(setDatasetLoading(false));
      }
    };

    fetchDatasetData();
  }, [projectId, dispatch]);

  // Fetch application setup data to get use cases if not available
  useEffect(() => {
    const fetchApplicationSetupData = async () => {
      if (!projectId || useCases.length > 0) return;

      try {
        const response = await axios.get(
          `${baseUrl}/application-setup?project_id=${projectId}`
        );
        if (response.data.success && response.data.data) {
          const { useCasesList } = response.data.data;

          if (useCasesList && useCasesList.length > 0) {
            console.log(
              "📋 Fetched use cases from application setup:",
              useCasesList
            );
            dispatch(setUseCases(useCasesList));
          }
        }
      } catch (err) {
        console.error("Error fetching application setup data:", err);
      }
    };

    fetchApplicationSetupData();
  }, [projectId, useCases.length, dispatch]);

  // Fetch metrics definition data if not available
  useEffect(() => {
    const fetchMetricsData = async () => {
      if (!projectId || (metrics.length > 0 && risks.length > 0)) return;

      try {
        const response = await axios.get(
          `${baseUrl}/metrics-definition?project_id=${projectId}`
        );
        if (response.data.success && response.data.data) {
          const {
            metrics: fetchedMetrics,
            risks: fetchedRisks,
            applicationSummary: fetchedSummary,
            trustWeights: fetchedWeights,
          } = response.data.data;

          if (fetchedMetrics && fetchedMetrics.length > 0) {
            dispatch(setMetrics(fetchedMetrics));
          }
          if (fetchedRisks && fetchedRisks.length > 0) {
            dispatch(setRisks(fetchedRisks));
          }
          if (fetchedSummary && Object.keys(fetchedSummary).length > 0) {
            dispatch(setApplicationSummary(fetchedSummary));
          }
          if (fetchedWeights && Object.keys(fetchedWeights).length > 0) {
            dispatch(setTrustWeights(fetchedWeights));
          }
        }
      } catch (err) {
        console.error("Error fetching metrics data:", err);
      }
    };

    fetchMetricsData();
  }, [projectId, metrics.length, risks.length, dispatch]);

  useEffect(() => {
    console.log("📌 Incoming Data:", {
      applicationSummary,
      useCases,
      metrics,
      risks,
    });
  }, []);

  /** Toggle use case selection */
  const toggleUseCaseSelection = (useCase) => {
    setSelectedUseCases((prev) => {
      const isSelected = prev.some((uc) => uc.title === useCase.title);
      if (isSelected) {
        return prev.filter((uc) => uc.title !== useCase.title);
      } else {
        return [...prev, useCase];
      }
    });
  };

  /** 📂 Handle File Upload */
  const handleFileUpload = (event) => {
    if (selectedUseCases.length === 0) {
      alert("Please select at least one use case first.");
      return;
    }

    const fileList = Array.from(event.target.files || []);
    const allowedTypes = [
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    ];

    const validFiles = fileList.filter((file) =>
      allowedTypes.includes(file.type)
    );

    // Check if any files were rejected
    const rejectedFiles = fileList.filter(
      (file) => !allowedTypes.includes(file.type)
    );
    if (rejectedFiles.length > 0) {
      alert(
        `Only .doc and .docx files are allowed. The following files were rejected:\n${rejectedFiles
          .map((f) => f.name)
          .join("\n")}`
      );
    }

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  /** ❌ Delete File */
  const handleDeleteFile = (idx) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  /** 📂 Handle Manual Test Suite Upload (.json, .csv, .xlsx, .xls) */
  const handleTestSuiteUpload = async (event) => {
    const fileList = Array.from(event.target.files || []);
    const allowedExtensions = [".json", ".csv", ".xlsx", ".xls"];

    const validFiles = fileList.filter((file) => {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
      return allowedExtensions.includes(ext);
    });

    const rejectedFiles = fileList.filter((file) => {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
      return !allowedExtensions.includes(ext);
    });

    if (rejectedFiles.length > 0) {
      alert(
        `Only .json, .csv, .xlsx, and .xls files are allowed. The following files were rejected:\n${rejectedFiles
          .map((f) => f.name)
          .join("\n")}`
      );
    }

    if (validFiles.length > 0) {
      setUploadedTestSuiteFiles((prev) => [...prev, ...validFiles]);

      // Parse each file and add to manualTestData
      for (const file of validFiles) {
        try {
          const parsedData = await parseTestSuiteFile(file);
          if (parsedData && parsedData.length > 0) {
            setManualTestData((prev) => [...prev, ...parsedData]);
            console.log(`Parsed ${parsedData.length} test cases from ${file.name}`);
          }
        } catch (err) {
          console.error(`Failed to parse ${file.name}:`, err);
          alert(`Failed to parse ${file.name}. Please check the file format.`);
        }
      }
    }

    // Reset the input
    event.target.value = "";
  };

  /** 🔧 Parse Test Suite File based on format */
  const parseTestSuiteFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

      reader.onload = (e) => {
        try {
          let data = [];
          const content = e.target.result;

          if (ext === ".json") {
            // Parse JSON file
            const jsonData = JSON.parse(content);
            data = Array.isArray(jsonData) ? jsonData : [jsonData];
          } else if (ext === ".csv") {
            // Parse CSV file
            data = parseCSV(content);
          } else if (ext === ".xlsx" || ext === ".xls") {
            // Parse Excel file
            const workbook = XLSX.read(content, { type: "binary" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            data = XLSX.utils.sheet_to_json(worksheet);
          }

          // Normalize the data to match qaData structure
          const normalizedData = data.map((item, index) => ({
            use_case: item.use_case || item.useCase || item["Use Case"] || "Manual Test",
            input_prompt: item.input_prompt || item.inputPrompt || item["Input Prompt"] || item.question || item.Question || "",
            expected_output: item.expected_output || item.expectedOutput || item["Expected Output"] || item.answer || item.Answer || item.expected_response || "",
            metrics: item.metrics || item.Metrics || [],
            difficulty: item.difficulty || item.Difficulty || "Medium",
            status: item.status || item.Status || "Draft",
            source_kb: item.source_kb || item.source || item.Source || "Manual Upload",
            policy_id: item.policy_id || item.policyId || item["Policy ID"] || "",
            isManual: true, // Flag to identify manually uploaded tests
          }));

          resolve(normalizedData);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));

      if (ext === ".xlsx" || ext === ".xls") {
        reader.readAsBinaryString(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  /** 📄 Parse CSV content */
  const parseCSV = (content) => {
    const lines = content.split("\n").filter((line) => line.trim());
    if (lines.length < 2) return [];

    // Get headers from first line
    const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));

    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx];
        });
        data.push(row);
      }
    }
    return data;
  };

  /** 📄 Parse a single CSV line (handles quoted values) */
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  /** ❌ Delete Uploaded Test Suite File */
  const handleDeleteTestSuiteFile = (idx) => {
    const fileToRemove = uploadedTestSuiteFiles[idx];
    setUploadedTestSuiteFiles((prev) => prev.filter((_, i) => i !== idx));

    // Remove the corresponding test data (based on source matching the file name)
    setManualTestData((prev) =>
      prev.filter((item) => item.source_kb !== fileToRemove.name && item.source_kb !== "Manual Upload")
    );
  };

  /** 🔀 Get combined test data (generated + manual) */
  const getCombinedTestData = () => {
    return [...qaData, ...manualTestData];
  };

  /** 📊 Get display data based on active tab */
  const getDisplayTestData = () => {
    switch (activeTestTab) {
      case 0: // All Tests
        return [...qaData, ...manualTestData];
      case 1: // Generated Tests
        return qaData;
      case 2: // Uploaded/Manual Tests
        return manualTestData;
      default:
        return qaData;
    }
  };

  const displayTestData = getDisplayTestData();

  const [anchorEl, setAnchorEl] = useState(null);
  const openFilterMenu = Boolean(anchorEl);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  /** 🚀 Trigger Backend Dataset Generation */
  const handleDatasetGeneration = async () => {
    if (!projectId) {
      dispatch(setDatasetError("No project selected"));
      alert("Please select a project before generating the dataset.");
      return;
    }

    if (selectedUseCases.length === 0) {
      alert(
        "Please select at least one use case before generating the dataset."
      );
      return;
    }

    if (uploadedFiles.length === 0) {
      alert(
        "Please upload at least one data source file before generating the dataset."
      );
      return;
    }

    try {
      console.log("📤 Sending dataset generation request...");
      setIsGenerating(true);
      dispatch(setDatasetLoading(true));
      dispatch(setDatasetError(null));

      const formData = new FormData();
      uploadedFiles.forEach((file) => formData.append("datasetFiles", file));
      formData.append(
        "payload",
        JSON.stringify({
          project_id: projectId,
          selectedUseCases,
          metrics,
          applicationSummary,
          trustWeights,
          risks,
        })
      );

      const response = await axios.post(
        `${baseUrl}/usecase-dataset-generation`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      console.log("📥 Backend response:", response.data);

      if (response.data.success && response.data.data) {
        // 🔍 DEBUG: Check first item from backend
        if (response.data.data.length > 0) {
          console.log("🔍 [DEBUG] First item from backend:");
          console.log("   - expected_response:", response.data.data[0].expected_response);
          console.log("   - expected_output:", response.data.data[0].expected_output);
          console.log("   - source_kb:", response.data.data[0].source_kb);
          console.log("   - policy_id:", response.data.data[0].policy_id);
          console.log("   - Full item:", response.data.data[0]);
        }

        // 🔧 Normalize the data to ensure consistent field names
        const normalized = response.data.data.map(normalizeDatasetItem);
        setLocalQaData(normalized);
        dispatch(setQaData(normalized));
        console.log("QA Dataset normalized and updated in frontend and Redux:", normalized);

        // 🔍 DEBUG: Check first normalized item
        if (normalized.length > 0) {
          console.log(" [DEBUG] First normalized item:");
          console.log("   - expected_output:", normalized[0].expected_output);
          console.log("   - source_kb:", normalized[0].source_kb);
          console.log("   - policy_id:", normalized[0].policy_id);
        }
      } else {
        console.warn("⚠ Backend responded without a QA dataset.");
        dispatch(
          setDatasetError(
            "Dataset generation failed. Please check the backend logs."
          )
        );
        alert("Dataset generation failed. Please check the backend logs.");
      }
    } catch (err) {
      console.error("❌ Dataset Generation Error:", err.message);
      dispatch(setDatasetError(err.message || "Dataset generation failed"));
      alert("Dataset generation failed. Please check the console for details.");
    } finally {
      setIsGenerating(false);
      dispatch(setDatasetLoading(false));
    }
  };

  /** 🧩 Handle Checkbox Toggle */
  const toggleRowSelection = (idx) => {
    setSelectedRows((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  /** 🎨 Render Difficulty Chip */
  const renderDifficultyChip = (difficulty) => {
    const colors = {
      Low: "success",
      Medium: "warning",
      High: "error",
      Critical: "error",

    };
    return <Chip label={difficulty} color={colors[difficulty]} size="small" />;
  };

  /** ➡️ Navigate to next page */
  const handleNext = () => {
    if (selectedRows.length === 0) {
      alert("Please select at least one dataset row before proceeding.");
      return;
    }

    // Use displayTestData to get selected items from the current tab view
    // This includes both generated (qaData) and uploaded (manualTestData) tests
    const currentDisplayData = getDisplayTestData();
    const selectedData = currentDisplayData.filter((_, idx) => selectedRows.includes(idx));

    console.log("📤 Navigating to Test Lab with selected tests:", {
      totalSelected: selectedData.length,
      fromGenerated: selectedData.filter(t => !t.isManual).length,
      fromManual: selectedData.filter(t => t.isManual).length,
    });

    navigate(`/project/${projectId}/test-evaluation`, {
      state: {
        qaData: selectedData,
        applicationSummary,
        useCases,
        dataEntriesCount: selectedData.length,
      },
    });
  };
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };
  const handleDownloadFile = async () => {
    if (qaData.length === 0) {
      alert("No dataset available to download.");
      return;
    }

    try {
      const now = new Date();
      const timestamp = `${String(now.getDate()).padStart(2, "0")}_${String(now.getMonth() + 1).padStart(2, "0")}_${String(now.getFullYear()).slice(-2)}_${String(now.getHours()).padStart(2, "0")}_${String(now.getMinutes()).padStart(2, "0")}_${String(now.getSeconds()).padStart(2, "0")}`;

      // Create table header row
      const headerRow = new DocxTableRow({
        children: [
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "S.No", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Use Case", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Input Prompt", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Expected Output", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Difficulty", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Status", bold: true, color: "FFFFFF" })] })],
            shading: { fill: "0094FB" },
          }),
        ],
      });

      // Create table body rows
      const bodyRows = qaData.map((item, index) =>
        new DocxTableRow({
          children: [
            new DocxTableCell({ children: [new Paragraph(String(index + 1))] }),
            new DocxTableCell({ children: [new Paragraph(String(item.use_case || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(item.input_prompt || item.question || "").substring(0, 100) + (String(item.input_prompt || item.question || "").length > 100 ? "..." : ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(item.expected_output || item.expected_response || item.answer || "").substring(0, 100) + (String(item.expected_output || item.expected_response || item.answer || "").length > 100 ? "..." : ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(item.difficulty || ""))] }),
            new DocxTableCell({ children: [new Paragraph(String(item.status || ""))] }),
          ],
        })
      );

      // Create document
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              // Header with Logo Text
              new Paragraph({
                children: [
                  new TextRun({
                    text: "ValidAIte",
                    bold: true,
                    size: 48,
                    color: "0094FB",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Trust First. Risk Aware. Enterprise Ready.",
                    italics: true,
                    size: 20,
                    color: "666666",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              }),
              // Horizontal line
              new Paragraph({
                border: {
                  bottom: { color: "0094FB", space: 1, style: BorderStyle.SINGLE, size: 6 },
                },
                spacing: { after: 400 },
              }),
              // Report Title
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Test Suite Dataset Report",
                    bold: true,
                    size: 36,
                    color: "1A1A1A",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 },
              }),
              // Report Info
              new Paragraph({
                children: [
                  new TextRun({ text: "Application: ", bold: true }),
                  new TextRun({ text: applicationSummary?.applicationName || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Archetype: ", bold: true }),
                  new TextRun({ text: applicationSummary?.archetypeCode || "N/A" }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Total Entries: ", bold: true }),
                  new TextRun({ text: String(qaData.length) }),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Generated: ", bold: true }),
                  new TextRun({ text: now.toLocaleString() }),
                ],
                spacing: { after: 300 },
              }),
              // Dataset Section
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Dataset Entries (${qaData.length})`,
                    bold: true,
                    size: 28,
                    color: "0094FB",
                  }),
                ],
                spacing: { before: 200, after: 200 },
              }),
              // Dataset Table
              new DocxTable({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [headerRow, ...bodyRows],
              }),
              // Footer
              new Paragraph({
                children: [
                  new TextRun({
                    text: "\n\n© ValidAIte - AI Governance & Risk Management Platform",
                    size: 18,
                    color: "888888",
                    italics: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 400 },
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Test_Suite_Dataset_Report_${timestamp}.docx`);
    } catch (err) {
      console.error("DOCX export failed:", err);
      alert("Failed to generate report. Please try again.");
    }
  };
  return (
    <Box
      sx={{
        bgcolor: "#F5F2F2",
        minHeight: "100vh",
        ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
      }}
    >
      {/* === Sticky Header === */}
      <Box
        sx={{
          position: "sticky",
          top: 50,
          zIndex: 120,
          backgroundColor: "#F5F2F2",
          py: 2.5,
          px: 3,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#111827">
          Test Suite
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Synthetic & Curated Evaluation Dataset Engineering...
        </Typography>
      </Box>

      {/* === Main Page Content === */}
      <Box sx={{ p: 3, pt: 2 }}>
        {/* Inputs Summary */}
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            /* Removed fullscreen from Inputs Card to keep it static */
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Box sx={{ mr: 1 }}>
              <InputsSVG style={{ width: 28, height: 28 }} />
            </Box>
            <Typography fontWeight={700} variant="h6">
              Inputs from Previous Stage
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Application
              </Typography>
              <Typography fontWeight={600}>
                {applicationSummary?.applicationName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Archetype Code
              </Typography>
              <Typography fontWeight={600}>
                {applicationSummary?.archetypeCode || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Risk Tier
              </Typography>
              <Typography fontWeight={700} color="error">
                {applicationSummary?.riskTier || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Risks Identified
              </Typography>
              <Typography fontWeight={700}>{risks.length}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Metrics ({metrics?.length || 0})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {metrics && metrics.length > 0 ? (
                  metrics.map((metric, idx) => (
                    <Chip
                      key={idx}
                      label={metric.metricName || metric.name}
                      size="small"
                      sx={{
                        bgcolor: "#E3F2FD",
                        color: "#1976d2",
                        fontWeight: 600,
                      }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No Metrics Found
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Modifiers:
            </Typography>
            <Chip label="Hallucination" size="small" sx={{ bgcolor: '#E3F2FD', color: '#1976d2' }} />
            <Chip label="KB Drift" size="small" sx={{ bgcolor: '#E3F2FD', color: '#1976d2' }} />
            <Chip label="P11 Exposure" size="small" sx={{ bgcolor: '#E3F2FD', color: '#1976d2' }} />
            <Chip label="Prompt Injection" size="small" sx={{ bgcolor: '#E3F2FD', color: '#1976d2' }} />
          </Box> */}
        </Card>

        {/* Use Cases Section */}
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            mb: 3,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: "pointer" }}
            onClick={() => setUseCasesExpanded(!useCasesExpanded)}
          >
            <Box display="flex" alignItems="center">
              <IconButton size="small">
                {useCasesExpanded ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowRightIcon />
                )}
              </IconButton>
              <Typography fontWeight={700} variant="h6">
                Use Cases*{" "}
                <Typography
                  component="span"
                  color="text.secondary"
                  variant="body2"
                >
                  (Select Relevant Use Cases)
                </Typography>
              </Typography>
            </Box>
          </Box>

          <Collapse in={useCasesExpanded}>
            <Box mt={2}>
              {useCases.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  {useCases.map((uc, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: selectedUseCases.some(
                          (selected) => selected.title === uc.title
                        )
                          ? "2px solid #1976d2"
                          : "1.5px solid #E5E7EB",
                        borderRadius: 2,
                        p: 2,
                        bgcolor: "#FFFFFF",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#1976d2",
                          boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={2}>
                        <Checkbox
                          checked={selectedUseCases.some(
                            (selected) => selected.title === uc.title
                          )}
                          onChange={() => toggleUseCaseSelection(uc)}
                        />

                        <Box>
                          <Chip
                            label={`UC-${String(idx + 1).padStart(2, "0")}`}
                            size="small"
                            sx={{
                              bgcolor: "#E7F0FF",
                              color: "#0A6CFF",
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          />
                          <Typography fontWeight={600}>
                            {uc.title || uc.fileName}
                          </Typography>
                          <Typography fontSize={13} color="#6B7280">
                            {uc.description || "No description provided"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No Use Cases Found
                </Typography>
              )}
            </Box>
          </Collapse>
        </Card>

        {/* Upload Data Source + Connect to RAG + Upload Manual Test Suite */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                opacity: selectedUseCases.length === 0 ? 0.5 : 1,
                pointerEvents: selectedUseCases.length === 0 ? "none" : "auto",
                height: "100%",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1}
                gap={1}
              >
                <DatasourceSVG style={{ width: 22, height: 22 }} />
                <Typography fontWeight={700} variant="h6">
                  Upload Data Source
                </Typography>
              </Box>

              {/* <Typography fontWeight={700} mb={1}>Upload Data Source</Typography> */}
              <Typography variant="body2" color="text.secondary" mb={2}>
                Upload Use case related KB doc/docx/pdf
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={
                  <DatasourceSVG
                    style={{
                      width: 22,
                      height: 22,
                      fill: "white",
                      stroke: "white",
                    }}
                  />
                }
                disabled={selectedUseCases.length === 0}
                sx={{
                  textTransform: "none",
                  bgcolor: "#0094FB",
                  "&:hover": { bgcolor: "#0084E1" },
                }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".doc,.docx"
                  onChange={handleFileUpload}
                />
              </Button>
              {selectedUseCases.length === 0 && (
                <Typography
                  variant="caption"
                  color="error"
                  display="block"
                  mt={1}
                >
                  * Select a use case first
                </Typography>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                opacity: selectedUseCases.length === 0 ? 0.5 : 1,
                pointerEvents: selectedUseCases.length === 0 ? "none" : "auto",
                height: "100%",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1}
                gap={1}
              >
                <UploadSVG style={{ width: 22, height: 22 }} />
                <Typography fontWeight={700} variant="h6">
                  Connect to RAG
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" mb={2}>
                Establish connection to AUT's KB
              </Typography>

              <Button
                variant="contained"
                component="label"
                startIcon={
                  <UploadSVG
                    style={{ width: 22, height: 22, fill: "#FFFFFF" }}
                  />
                }
                disabled={selectedUseCases.length === 0}
                sx={{
                  textTransform: "none",
                  bgcolor: "#0094FB",
                  color: "#FFFFFF",
                  stroke: "#ffffff !important",
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#0084E1" },
                  "&.Mui-disabled": {
                    bgcolor: "#D1D5DB",
                    color: "#9CA3AF",
                  },
                }}
              >
                Connect RAG
              </Button>

              {selectedUseCases.length === 0 && (
                <Typography
                  variant="caption"
                  color="error"
                  display="block"
                  mt={1}
                >
                  * Select a use case first
                </Typography>
              )}
            </Card>
          </Grid>

          {/* Upload Manual Test Suite Card - Always Enabled */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                height: "100%",
                border: uploadedTestSuiteFiles.length > 0 ? "2px solid #10B981" : "1px solid #E5E7EB",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1}
                gap={1}
              >
                <CloudUploadIcon sx={{ color: "#0094FB", fontSize: 22 }} />
                <Typography fontWeight={700} variant="h6">
                  Upload Test Suite
                </Typography>
                <Chip
                  label="Optional"
                  size="small"
                  sx={{
                    bgcolor: "#E3F2FD",
                    color: "#1976d2",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    height: 20,
                  }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" mb={2}>
                Upload manual test suite (.json, .csv, .xlsx)
              </Typography>

              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  textTransform: "none",
                  bgcolor: "#0094FB",
                  color: "#FFFFFF",
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#0084E1" },
                }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".json,.csv,.xlsx,.xls"
                  onChange={handleTestSuiteUpload}
                />
              </Button>

              {uploadedTestSuiteFiles.length > 0 && (
                <Typography
                  variant="caption"
                  color="success.main"
                  display="block"
                  mt={1}
                  fontWeight={600}
                >
                  ✓ {manualTestData.length} tests loaded
                </Typography>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* Uploaded Data */}
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            mb: 3,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          }}
        >
          <Typography fontWeight={700} variant="h6" mb={2}>
            Uploaded Data
          </Typography>

          {uploadedFiles.length === 0 ? (
            <Box
              textAlign="center"
              py={5}
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#E3F2FD",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <ErrorIcon style={{ width: 32, height: 32 }} />
              </Box>
              <Typography fontWeight={600} mb={1}>
                No Data Files have been uploaded yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Upload File" or add connect RAG to Generate Datasets
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {uploadedFiles.map((file, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    p: 2,
                    bgcolor: "#FAFAFA",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <UploadFileIcon color="primary" />
                    <Box>
                      <Typography fontWeight={600}>{file.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteFile(idx)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Card>

        {/* Generate Dataset Button */}
        <Box display="flex" justifyContent="flex-start" mb={3}>
          <Button
            variant="contained"
            size="large"
            onClick={handleDatasetGeneration}
            disabled={
              isGenerating ||
              selectedUseCases.length === 0 ||
              uploadedFiles.length === 0
            }
            sx={{
              px: 4,
              py: 1.2,
              textTransform: "none",
              bgcolor: "#0094FB",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#0084E1" },
              "&.Mui-disabled": {
                bgcolor: "#D1D5DB",
                color: "#9CA3AF",
              },
            }}
          >
            {isGenerating
              ? "Generating Test Suite..."
              : "Generate Test Suite"}
          </Button>
        </Box>

        {/* Test Suite Table - Shows both Generated and Manual tests */}
        {(qaData.length > 0 || manualTestData.length > 0) && (
          <Card
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              /* Fullscreen styles applied to the Dataset Table card */
              position: isFullScreen ? "fixed" : "relative",
              top: isFullScreen ? 0 : "auto",
              left: isFullScreen ? 0 : "auto",
              width: isFullScreen ? "100vw" : "100%",
              height: isFullScreen ? "100vh" : "auto",
              zIndex: isFullScreen ? 3000 : "auto",
              backgroundColor: "#F6F3F2f",
              overflow: isFullScreen ? "auto" : "visible",
            }}
          >
            {/* Tabs to switch between All, Generated, and Manual tests */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTestTab}
                onChange={(_, newValue) => {
                  setActiveTestTab(newValue);
                  setSelectedRows([]); // Reset selection when switching tabs
                }}
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  },
                }}
              >
                <Tab
                  label={`All Tests (${qaData.length + manualTestData.length})`}
                  sx={{ color: activeTestTab === 0 ? "#0094FB" : "#6B7280" }}
                />
                <Tab
                  label={`Generated (${qaData.length})`}
                  sx={{ color: activeTestTab === 1 ? "#0094FB" : "#6B7280" }}
                />
                <Tab
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <span>Uploaded ({manualTestData.length})</span>
                      {manualTestData.length > 0 && (
                        <Chip
                          label="Manual"
                          size="small"
                          sx={{
                            bgcolor: "#FEF3C7",
                            color: "#92400E",
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            height: 18,
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{ color: activeTestTab === 2 ? "#0094FB" : "#6B7280" }}
                />
              </Tabs>
            </Box>

            <Typography fontWeight={700} variant="h6" mb={2}>
              {activeTestTab === 0
                ? `All Test Cases (${qaData.length + manualTestData.length} entries)`
                : activeTestTab === 1
                ? `Generated Test Suite (${qaData.length} entries)`
                : `Uploaded Test Suite (${manualTestData.length} entries)`}
            </Typography>

            {/* Search + Filter + Fullscreen (FIGMA EXACT) */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                mb: 2,
                position: "sticky",
                top: isFullScreen ? 0 : "auto",
                background: "#fff",
                zIndex: 10,
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
            >
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  width: "260px",
                  backgroundColor: "#fff",
                }}
              >
                <span
                  style={{
                    color: "#6B7280",
                    fontSize: "18px",
                    marginRight: "8px",
                  }}
                >
          
                </span>

                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => console.log(e.target.value)}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    fontSize: "14px",
                  }}
                />
              </Box>

              {/* Filter Button */}
              <Button
                variant="outlined"
                onClick={handleFilterClick}
                sx={{
                  textTransform: "none",
                  borderColor: "#D1D5DB",
                  color: "#374151",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  background: "#fff",
                }}
              >
                <FilterListIcon sx={{ fontSize: 18 }} />
                <span style={{ fontSize: "14px" }}>Filter</span>
                <ArrowDropDownIcon />
              </Button>

              {/* Fullscreen Button */}
              <IconButton
                onClick={toggleFullScreen}
                sx={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  padding: "6px",
                  background: "#fff",
                  "&:hover": {
                    background: "#E3F2FD",
                    borderColor: "#0094FB",
                  },
                }}
              >
                {isFullScreen ? (
                  <span style={{ fontSize: "18px" }}>🗗</span>
                ) : (
                  <span style={{ fontSize: "18px" }}>🗖</span>
                )}
              </IconButton>

              {/* Filter Dropdown */}
              <Menu
                anchorEl={anchorEl}
                open={openFilterMenu}
                onClose={handleFilterClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <MenuItem onClick={handleFilterClose}>Difficulty: Low</MenuItem>
                <MenuItem onClick={handleFilterClose}>
                  Difficulty: Medium
                </MenuItem>
                <MenuItem onClick={handleFilterClose}>
                  Difficulty: High
                </MenuItem>
                <MenuItem onClick={handleFilterClose}>
                  Difficulty: Critical
                </MenuItem>


                <MenuItem onClick={handleFilterClose}>Status: Draft</MenuItem>
                <MenuItem onClick={handleFilterClose}>
                  Status: Completed
                </MenuItem>

                <MenuItem onClick={handleFilterClose}>
                  Source: Knowledge Base
                </MenuItem>
                <MenuItem onClick={handleFilterClose}>
                  Source: Document
                </MenuItem>
              </Menu>
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.9rem",
                }}
              >
                <thead
                  style={{
                    background: "#F9FAFB",
                    position: "sticky",
                    top: isFullScreen ? 60 : 0,
                    zIndex: 5,
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      <Checkbox
                        checked={selectedRows.length === displayTestData.length && displayTestData.length > 0}
                        onChange={() =>
                          setSelectedRows(
                            selectedRows.length === displayTestData.length
                              ? []
                              : displayTestData.map((_, i) => i)
                          )
                        }
                      />
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Use Case
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Metrics
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Input Prompt
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Ground Truth
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Difficulty
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Source
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        overflow: "hidden",
                      }}
                    >
                      Actions
                    </th>{" "}
                  </tr>
                </thead>

                <tbody>
                  {displayTestData.map((row, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #E5E7EB",
                        backgroundColor: selectedRows.includes(idx)
                          ? "#F0F9FF"
                          : row.isManual
                          ? "#FFFBEB"
                          : "#FFF",
                      }}
                    >
                      <td style={{ padding: "12px" }}>
                        <Checkbox
                          checked={selectedRows.includes(idx)}
                          onChange={() => toggleRowSelection(idx)}
                        />
                      </td>

                      <td style={{ padding: "12px" }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {row.use_case}
                          {row.isManual && (
                            <Chip
                              label="Manual"
                              size="small"
                              sx={{
                                bgcolor: "#FEF3C7",
                                color: "#92400E",
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                height: 18,
                              }}
                            />
                          )}
                        </Box>
                      </td>

                      <td style={{ padding: "12px" }}>
                        {Array.isArray(row.metrics)
                          ? row.metrics.join(", ")
                          : row.metrics}
                      </td>

                      <td
                        style={{
                          padding: "21px 10px 10px",
                          maxWidth: isFullScreen ? "100%" : 200,
                          whiteSpace: isFullScreen ? "normal" : "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          wordBreak: isFullScreen ? "break-word" : "normal",
                        }}
                      >
                        {isFullScreen ? (
                          <Typography
                            variant="body2"
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {row.input_prompt}
                          </Typography>
                        ) : (
                          <Tooltip title={row.input_prompt} arrow>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: "pointer",
                                maxWidth: "200px",
                              }}
                            >
                              {row.input_prompt}
                            </Typography>
                          </Tooltip>
                        )}
                      </td>

                      <td
                        style={{
                          padding: "12px",
                          maxWidth: isFullScreen ? "100%" : 200,
                          whiteSpace: isFullScreen ? "normal" : "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          wordBreak: isFullScreen ? "break-word" : "normal",
                        }}
                      >
                        {isFullScreen ? (
                          <Typography
                            variant="body2"
                            style={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {row.expected_output}
                          </Typography>
                        ) : (
                          <Tooltip title={row.expected_output} arrow>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: "pointer",
                                maxWidth: "200px",
                              }}
                            >
                              {row.expected_output}
                            </Typography>
                          </Tooltip>
                        )}
                      </td>

                      <td style={{ padding: "12px" }}>
                        {renderDifficultyChip(row.difficulty)}
                      </td>

                      <td style={{ padding: "12px" }}>
                        <Typography variant="body2" noWrap>
                          {row.source_kb || row.source || "Knowledge Base"}
                        </Typography>
                      </td>

                      <td style={{ padding: "12px" }}>{row.status}</td>

                      <td style={{ width: "120px", textAlign: "center" }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setQaData((prev) =>
                                prev.filter((_, i) => i !== idx)
                              )
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {/* Next Button */}
          <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
               <Button
    variant="outlined"
    startIcon={<DownloadSVG style={{ width: 20, height: 20 }} />}
    onClick={handleDownloadFile}
    sx={{
      borderColor: "#3B82F6",
      color: "#3B82F6",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 2,
      px: 3,
      py: 1.2,
      "&:hover": { bgcolor: "#EBF5FF", borderColor: "#2563EB" },
    }}
  >
    Download File
  </Button>
 
              <Button
                variant="contained"
                size="large"
                onClick={handleNext}
                disabled={selectedRows.length === 0}
                sx={{
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  bgcolor: "#0094FB",
                  "&:hover": { bgcolor: "#0084E1" },
                  "&.Mui-disabled": {
                    bgcolor: "#D1D5DB",
                    color: "#9CA3AF",
                  },
                }}
              >
                Go to Test Lab →
              </Button>
            </Box>
          </Card>
        )}
      </Box>

      {/* Loading Overlay */}
      <LoadingOverlay open={isGenerating} message="Generating Test Suite..." />
    </Box>
  );
};

export default UseCaseDatasetGenerationPage;
